import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ErrorBoundary, PageSkeleton } from '@miror/ui-web';
import { useAuth } from '../contexts/AuthContext';
import AppLayout, { RequirePermission } from '../components/layout/AppLayout';

const LoginPage = React.lazy(() => import('../pages/auth/LoginPage'));
const SsoCallbackPage = React.lazy(() => import('../pages/auth/SsoCallbackPage'));
const DashboardPage = React.lazy(() => import('../pages/dashboard/DashboardPage'));
const ClientDashboard = React.lazy(() => import('../pages/dashboard/ClientDashboard'));
const TasksPage = React.lazy(() => import('../pages/tasks/TasksPage'));
const TaskDetailPage = React.lazy(() => import('../pages/tasks/TaskDetailPage'));
const RecurrencesPage = React.lazy(() => import('../pages/tasks/RecurrencesPage'));
const ProjectsPage = React.lazy(() => import('../pages/projects/ProjectsPage'));
const ProjectDetailPage = React.lazy(() => import('../pages/projects/ProjectDetailPage'));
// Empresa — módulo admin padrão da suíte (EmpresaAdminShell do @miror/admin-ui-web)
const EmpresaPage = React.lazy(() => import('../pages/empresa/EmpresaPage'));
const PlanoUsoPage = React.lazy(() => import('../pages/plano-uso/PlanoUsoPage'));
const IntegracoesPage = React.lazy(() => import('../pages/integracoes/IntegracoesPage'));
const ClientesPage = React.lazy(() => import('../pages/clientes/ClientesPage'));
// Painel Miror — super admin (a página se auto-gateia)
const PainelMirorPage = React.lazy(() => import('../pages/painel-miror/PainelMirorPage'));
// Equipe — gestão de membros via Core (mesma solução do Miror Pro)
const UsersPage = React.lazy(() => import('../pages/users/EquipePage'));
const CalendarPage = React.lazy(() => import('../pages/calendar/CalendarPage'));
const SettingsPage = React.lazy(() => import('../pages/settings/SettingsPage'));
const HoursRegisterPage = React.lazy(() => import('../pages/horas/HoursRegisterPage'));
const HoursDashboardPage = React.lazy(() => import('../pages/horas/HoursDashboardPage'));
const HoursAdminPage = React.lazy(() => import('../pages/horas/HoursAdminPage'));
const TvPage = React.lazy(() => import('../pages/tasks/TvPage'));

/**
 * Dashboard adapta-se ao papel: cliente vê o painel reduzido. Mantido como
 * elemento da rota '/' e '/dashboard'.
 */
const DashboardRouter: React.FC = () => {
  const { user } = useAuth();
  return user?.permissao === 'cliente' ? <ClientDashboard /> : <DashboardPage />;
};

const AppRoutes: React.FC = () => {
  return (
    <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <ErrorBoundary>
        <React.Suspense fallback={<PageSkeleton variant="dashboard" />}>
          <Routes>
            {/* Rotas públicas — sem shell */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/sso/callback" element={<SsoCallbackPage />} />
            <Route path="/tv" element={<TvPage />} />

            {/* Rotas protegidas — AppLayout faz o gate de auth e renderiza o
                shell uma única vez; apenas o <Outlet/> troca na navegação. */}
            <Route element={<AppLayout />}>
              <Route path="/" element={<DashboardRouter />} />
              <Route path="/dashboard" element={<DashboardRouter />} />

              <Route
                path="/tasks"
                element={
                  <RequirePermission permission="task:tasks:view">
                    <TasksPage />
                  </RequirePermission>
                }
              />
              <Route
                path="/tasks/recorrencias"
                element={
                  <RequirePermission permission="task:recurrences:view">
                    <RecurrencesPage />
                  </RequirePermission>
                }
              />
              <Route
                path="/tasks/:id"
                element={
                  <RequirePermission permission="task:tasks:view">
                    <TaskDetailPage />
                  </RequirePermission>
                }
              />

              <Route
                path="/projects"
                element={
                  <RequirePermission permission="task:projects:view">
                    <ProjectsPage />
                  </RequirePermission>
                }
              />
              <Route
                path="/projects/:id"
                element={
                  <RequirePermission permission="task:projects:view">
                    <ProjectDetailPage />
                  </RequirePermission>
                }
              />

              <Route
                path="/clientes"
                element={
                  <RequirePermission permission="task:clients:view">
                    <ClientesPage />
                  </RequirePermission>
                }
              />

              <Route
                path="/calendar"
                element={
                  <RequirePermission permission="task:calendar:view">
                    <CalendarPage />
                  </RequirePermission>
                }
              />
              {/* Minha conta é universal — sem gate (igual ao web do Pro). */}
              <Route path="/settings" element={<SettingsPage />} />

              <Route
                path="/empresa"
                element={
                  <RequirePermission permission="task:company:view">
                    <EmpresaPage />
                  </RequirePermission>
                }
              />
              <Route
                path="/integracoes"
                element={
                  <RequirePermission permission="task:integrations:view">
                    <IntegracoesPage />
                  </RequirePermission>
                }
              />
              <Route
                path="/plano-uso"
                element={
                  <RequirePermission permission="task:billing:view">
                    <PlanoUsoPage />
                  </RequirePermission>
                }
              />
              {/* Painel Miror se auto-gateia por super_admin. */}
              <Route path="/painel-miror" element={<PainelMirorPage />} />
              <Route
                path="/users"
                element={
                  <RequirePermission permission="task:team:view">
                    <UsersPage />
                  </RequirePermission>
                }
              />
              <Route
                path="/horas"
                element={
                  <RequirePermission permission="task:hours:view">
                    <HoursRegisterPage />
                  </RequirePermission>
                }
              />
              <Route
                path="/horas/dashboard"
                element={
                  <RequirePermission permission="task:hours:view">
                    <HoursDashboardPage />
                  </RequirePermission>
                }
              />
              <Route
                path="/horas/admin"
                element={
                  <RequirePermission permission="task:hours:manage">
                    <HoursAdminPage />
                  </RequirePermission>
                }
              />
            </Route>

            {/* 404 → dashboard */}
            <Route path="*" element={<Navigate to="/dashboard" />} />
          </Routes>
        </React.Suspense>
      </ErrorBoundary>
    </BrowserRouter>
  );
};

export default AppRoutes;
